import React, { useContext, useMemo } from 'react';
import { LangContext } from '../../../contexts/langContext';
import { nakshatra_yoni_dictionary, yoni_dictionary } from '../../../static/jyotish_local';
import { getJyotish } from '../../Edu/utilsJyotish';
import { AvatarName } from '../AvatarName';

const resolveYoniEntry = (yoniId) => {
  if (!yoniId) return null;
  return yoni_dictionary[yoniId]
    || Object.values(yoni_dictionary).find((y) => y.id === yoniId)
    || null;
};

const getYoniFromAvatar = (avatar) => {
  if (!avatar?.dob || !avatar?.aggr?.city?.location) return null;
  try {
    const result = getJyotish(avatar);
    const nakshatra = result?.kundli?.dasha?.birthNakshatra;
    if (!nakshatra || typeof nakshatra !== 'string') return null;
    const normalized = nakshatra.toLowerCase().replace(/\s+/g, '_');
    const fromNak = nakshatra_yoni_dictionary[normalized];
    if (!fromNak?.yoniId) return null;
    const entry = resolveYoniEntry(fromNak.yoniId);
    if (!entry) return null;
    return {
      id: entry.id || fromNak.yoniId,
      emoji: entry.emoji || fromNak.yoniEmoji,
      order: fromNak.order,
      sanskrit: entry.sanskrit || '',
      image: entry.image || null,
      entry,
    };
  } catch {
    return null;
  }
};

/**
 * Compact per-person yoni header matching the /yoni result look:
 * constellation image + Devanagari + transliteration + animal title + description.
 */
export function YoniPersonHero({ avatar, user, children }) {
  const { language = 'en' } = useContext(LangContext) || {};

  const yoni = useMemo(
    () => getYoniFromAvatar(avatar),
    [avatar?.dob, avatar?.design?.meta?.personality, avatar?.aggr?.city?.location],
  );

  const title = yoni?.entry?.[`title-${language}`] || yoni?.entry?.['title-en'] || '';
  const titleSanskrit =
    yoni?.entry?.[`title_sanskrit-${language}`] || yoni?.entry?.['title_sanskrit-en'] || '';
  const description =
    yoni?.entry?.[`description-${language}`] || yoni?.entry?.['description-en'] || '';
  const imageSrc = yoni?.image
    ? `${process.env.PUBLIC_URL || ''}${yoni.image}`
    : null;

  return (
    <div className="yoni_person_hero">
      {imageSrc && (
        <img
          src={imageSrc}
          alt=""
          aria-hidden="true"
          className="yoni_person_hero_bg"
        />
      )}

      <div className="yoni_person_hero_copy">
        {yoni?.sanskrit && (
          <div className="yoni_person_hero_sanskrit">{yoni.sanskrit}</div>
        )}
        {titleSanskrit && (
          <div className="yoni_person_hero_translit">{titleSanskrit}</div>
        )}
        {title && (
          <div className="yoni_person_hero_title">{title}</div>
        )}
        {description && (
          <div className="yoni_person_hero_desc">{description}</div>
        )}
        {!yoni && (
          <div className="yoni_person_hero_fallback">
            <AvatarName avatar={avatar} />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
